import { useEffect, useState } from "react";
import { Table, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import api from "../lib/api";

const PointsTableSection = () => {
  const [standings, setStandings] = useState(null);

  useEffect(() => {
    api.get("/standings").then((r) => setStandings(r.data)).catch(() => setStandings([]));
  }, []);

  const groups = {};
  (standings || []).forEach((s) => {
    const key = s.league || "Boys";
    (groups[key] = groups[key] || []).push(s);
  });
  const leagueNames = Object.keys(groups).sort();

  return (
    <section id="points-table" className="relative py-10 md:py-14">
      <Reveal>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tighter flex items-center gap-3">
          <Table className="h-6 w-6 text-flame" /> The points tables
        </h2>
        <p className="mt-2 text-muted-foreground">
          Every league I'm tracking — girls and boys, sorted by points, updated after every weekend.
        </p>
      </Reveal>

      {standings === null ? (
        <div className="mt-8 grid place-items-center py-12 text-muted-foreground" data-testid="standings-loading">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : standings.length === 0 ? (
        <div className="mt-8 rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="standings-empty">
          <Table className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="mt-3 font-display font-bold text-lg">The tables haven't started yet</p>
          <p className="text-sm text-muted-foreground mt-1">Teams will appear here once the season kicks off.</p>
        </div>
      ) : (
        leagueNames.map((league, li) => {
          const rows = groups[league];
          return (
            <div key={league} className="mt-8">
              <Reveal delay={0.03 * li}>
                <h3 className="font-display text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2.5" data-testid="points-table-league-title">
                  <span className="h-2.5 w-2.5 rounded-full bg-flame" aria-hidden="true" /> {league}
                </h3>
              </Reveal>
              <Reveal delay={0.05 + 0.03 * li}>
                <div className="mt-4 overflow-x-auto rounded-3xl bg-card border border-foreground/10 shadow-sm p-2 md:p-4" data-testid="points-table">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead>
                      <tr className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <th className="py-3 px-2 text-left font-medium">#</th>
                        <th className="py-3 px-2 text-left font-medium">Team</th>
                        <th className="py-3 px-2 text-center font-medium">P</th>
                        <th className="py-3 px-2 text-center font-medium">W</th>
                        <th className="py-3 px-2 text-center font-medium">D</th>
                        <th className="py-3 px-2 text-center font-medium">L</th>
                        <th className="py-3 px-2 text-center font-medium">Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((s, i) => (
                        <tr key={s.id} className={`border-t border-foreground/5 ${i === 0 ? "bg-turf/10" : ""}`} data-testid="points-table-row">
                          <td className="py-3 px-2 font-mono text-xs">{i + 1}</td>
                          <td className="py-3 px-2 font-semibold">{s.team}</td>
                          <td className="py-3 px-2 text-center font-mono text-xs">{s.played}</td>
                          <td className="py-3 px-2 text-center font-mono text-xs">{s.won}</td>
                          <td className="py-3 px-2 text-center font-mono text-xs">{s.drawn}</td>
                          <td className="py-3 px-2 text-center font-mono text-xs">{s.lost}</td>
                          <td className="py-3 px-2 text-center font-display font-bold text-pitch dark:text-turf">{s.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">P = played · W = won · D = drawn · L = lost · Pts = points (3 for a win, 1 for a draw)</p>
              </Reveal>
            </div>
          );
        })
      )}
    </section>
  );
};

export default PointsTableSection;
